'use client';

import { useState, useEffect, useRef, createContext, useContext } from 'react';
import { usePathname } from 'next/navigation';

// Debug logging — set to true to re-enable the verbose [ExternalContentLoader] / [Laravel Stub] console output.
const ECL_DEBUG = false;
const eclLog = (...args) => { if (ECL_DEBUG) console.log(...args); };

// Context for external content management
const ExternalContentContext = createContext();

export function useExternalContent() {
  return useContext(ExternalContentContext);
}

export function ExternalContentLoader({ children }) {
  const pathname = usePathname();
  const [laravelHtmlContent, setLaravelHtmlContent] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentUrl, setCurrentUrl] = useState(null);
  const [cssLoaded, setCssLoaded] = useState(false);
  const contentRef = useRef(null);
  const loadingRef = useRef(false); // Prevent duplicate loads
  const loadContentRef = useRef(null); // Ref to always get latest loadContent function

  // 🔥 CRITICAL FIX: Clear Laravel content when navigating to internal Next.js routes
  // This ensures React children (like Dashboard) are shown instead of stale Laravel content
  useEffect(() => {
    // Only clear if we have Laravel content AND we're NOT on an /xsite/ route
    if (laravelHtmlContent && pathname && !pathname.startsWith('/xsite/')) {
      eclLog('[ExternalContentLoader] 🧹 Clearing Laravel content for internal route:', pathname);
      setLaravelHtmlContent(null);
      setCurrentUrl(null);
      setError(null);
    }
  }, [pathname]); // Only watch pathname - laravelHtmlContent is intentionally not in deps to avoid loops

  // Initialize global functions for backward compatibility
  useEffect(() => {
    if (typeof window === 'undefined') return;
    
    // Global function to show Laravel content
    window.showGlobalContent = (htmlContent) => {
      // eclLog('[ExternalContentLoader] 🎯 Showing Laravel content via React state');
      setLaravelHtmlContent(htmlContent);
    };
    
    // Global function to clear content
    window.clearGlobalContent = () => {
      // eclLog('[ExternalContentLoader] 🧹 Clearing Laravel content via React state');
      setLaravelHtmlContent(null);
    };
    
    // Helper function to hide React content
    window.hideReactContent = () => {
      // eclLog('[ExternalContentLoader] 👻 React content will be hidden when Laravel content is shown');
      // This is now handled by conditional rendering
    };
    
    // Helper function to restore React content
    window.restoreReactContent = () => {
      // eclLog('[ExternalContentLoader] 👁️ React content will be restored when Laravel content is cleared');
      // This is now handled by conditional rendering
    };

    // ✅ FIX: Initialize global variables required by Laravel scripts
    // msg_counter - Used by sw.js for notification count (line 166)
    window.msg_counter = window.msg_counter || JSON.stringify(0);

    // ✅ FIX: Create stubs for missing Laravel JavaScript functions (from invoice.js)
    // These functions are called by inline scripts in Laravel HTML but not always loaded
    // Creating stubs prevents "ReferenceError: X is not defined" console errors
    // NOTE: These stubs ALWAYS override any existing functions to ensure consistent behavior

    // Function: setFoundCases() - Display case count in DataTable info
    window.setFoundCases = function() {
      eclLog('[Laravel Stub] setFoundCases() called');
      try {
        const table = window.$('#table111').dataTable().api();
        const casesCnt = table.ajax.json()?.recordsCases;
        if (casesCnt) {
          window.$('#table111_info').html(window.$('#table111_info').html() + '<br>' + casesCnt + ' Vorgänge gefunden');
        }
      } catch (e) {
        console.warn('[Laravel Stub] setFoundCases: DataTable not ready or not found');
      }
    };

    // Function: afterload() - Post-load initialization
    window.afterload = function() {
      eclLog('[Laravel Stub] afterload() called');
      try {
        if (typeof window.checkClicks === 'function') window.checkClicks();
        if (typeof window.buildButtons === 'function') window.buildButtons();
      } catch (e) {
        console.warn('[Laravel Stub] afterload: Helper functions not available');
      }
    };

    // Function: doInvoice() - Invoice creation handler
    window.doInvoice = function(xClick) {
      eclLog('[Laravel Stub] doInvoice() called', xClick);
      // Stub - real implementation would handle invoice creation
    };

    // Function: doInvoiceComplete() - Invoice finalization
    window.doInvoiceComplete = function(xClick) {
      eclLog('[Laravel Stub] doInvoiceComplete() called', xClick);
    };

    // Function: doPackinglist() - Packing list generation
    window.doPackinglist = function() {
      eclLog('[Laravel Stub] doPackinglist() called');
    };

    // Function: getSelectedRowData() - Get selected DataTable rows
    window.getSelectedRowData = function() {
      eclLog('[Laravel Stub] getSelectedRowData() called');
      try {
        const table = window.$('#table113').DataTable();
        const rows = table.rows({ selected: true }).count();
        if (!rows) {
          console.warn('[Laravel Stub] No rows selected');
          return false;
        }
        return table.rows({ selected: true }).data();
      } catch (e) {
        console.warn('[Laravel Stub] getSelectedRowData: DataTable not available');
        return false;
      }
    };

    // Function: getIdsFromSelection() - Extract IDs from selected rows
    window.getIdsFromSelection = function(pickingList = false) {
      eclLog('[Laravel Stub] getIdsFromSelection() called, pickingList:', pickingList);
      const data = window.getSelectedRowData?.() || false;
      if (!data) return false;

      const tmp = {};
      for (let i = 0; i < data.length; i++) {
        tmp[pickingList ? data[i].picking_id : data[i].id] = 1;
      }
      return Object.keys(tmp);
    };

    // Function: addDiscount() - Add discount to offer
    window.addDiscount = function(xClick) {
      eclLog('[Laravel Stub] addDiscount() called', xClick);
    };

    // Function: setOfferType() - Set offer type
    window.setOfferType = function(xClick, type, field = 'invoice_offer_accepted') {
      eclLog('[Laravel Stub] setOfferType() called', { xClick, type, field });
    };

    // Function: reloadTables() - Reload DataTables
    window.reloadTables = function() {
      eclLog('[Laravel Stub] reloadTables() called');
      try {
        if (window.$('#modaldiv').modal) window.$('#modaldiv').modal('hide');
        if (window.$('#table111').DataTable) window.$('#table111').DataTable().draw();
        if (window.$('#table128').DataTable) window.$('#table128').DataTable().draw();
        if (window.$('#table113').DataTable) window.$('#table113').DataTable().draw();
      } catch (e) {
        console.warn('[Laravel Stub] reloadTables: DataTables not available');
      }
    };

    // Function: load_pdf() - Open PDF viewer
    window.load_pdf = function(id) {
      eclLog('[Laravel Stub] load_pdf() called with id:', id);
      if (id) {
        window.open('show_picture.html?f=2&pic=' + id, '_blank');
      }
    };

    // Function: rechnungskreisWarnung() - Billing circle warning
    window.rechnungskreisWarnung = function() {
      eclLog('[Laravel Stub] rechnungskreisWarnung() called');
      alert('Achtung, es wird ein anderer Rechnungskreis verwendet. Bei Storno wird keine EMail verschickt');
    };

    // ✅ FIX: KTComponents stub - Keenthemes component library expected by app.js
    // app.js:2040 calls KTComponents.init() which doesn't exist in our setup
    if (!window.KTComponents) {
      window.KTComponents = {
        init: function() {
          eclLog('[Laravel Stub] KTComponents.init() called');
        }
      };
    }

    // ✅ FIX: refreshFsLightbox stub - FsLightbox gallery refresh function
    if (!window.refreshFsLightbox) {
      window.refreshFsLightbox = function() {
        eclLog('[Laravel Stub] refreshFsLightbox() called');
      };
    }

  }, []);

  // Helper: CSS laden und auf .xsite-content-container scopen
  const loadAndScopeCSS = async (cssPath, styleId, name) => {
    try {
      const response = await fetch(cssPath);
      const cssText = await response.text();

      // CSS scopen: Alle Selektoren mit .xsite-content-container prefixen
      const scopedCSS = cssText.replace(
        /(^|[,\n\r])\s*([^@}]+)\s*\{/g,
        (match, prefix, selector) => {
          // Skip @-rules (media queries, keyframes, etc.)
          if (selector.trim().startsWith('@')) {
            return match;
          }

          let trimmedSelector = selector.trim();

          // Spezielle Behandlung für body/html Selektoren
          // "body" → ".xsite-content-container" (direkt ersetzen, nicht prefixen!)
          if (trimmedSelector === 'body' || trimmedSelector === 'html') {
            return `${prefix}.xsite-content-container {`;
          }

          // "body .foo" → ".xsite-content-container .foo" (body entfernen, container hinzufügen)
          if (trimmedSelector.startsWith('body ') || trimmedSelector.startsWith('html ')) {
            trimmedSelector = trimmedSelector.replace(/^(body|html)\s+/, '');
            return `${prefix}.xsite-content-container ${trimmedSelector} {`;
          }

          // Alle anderen Selektoren: Normal prefixen
          return `${prefix}.xsite-content-container ${trimmedSelector} {`;
        }
      );

      // Style-Tag erstellen und in HEAD einfügen
      const styleTag = document.createElement('style');
      styleTag.id = styleId;
      styleTag.textContent = scopedCSS;
      document.head.appendChild(styleTag);

      eclLog(`[ExternalContentLoader] 📎 Loaded & scoped ${name} CSS`);
    } catch (error) {
      console.error(`[ExternalContentLoader] ❌ Failed to load ${name} CSS:`, error);
    }
  };

  // Load external CSS for Laravel content
  const loadExternalCSS = async () => {
    if (cssLoaded || typeof document === 'undefined') return;

    eclLog('[ExternalContentLoader] 🎨 Loading dedicated Laravel content CSS...');

    // 0. CSS Reset für Container - Überschreibt Next.js Global Styles
    if (!document.getElementById('xsite-container-reset')) {
      const resetStyle = document.createElement('style');
      resetStyle.id = 'xsite-container-reset';
      resetStyle.textContent = `
        /* Reset Next.js/Tailwind global styles für Laravel Container */
        .xsite-content-container {
          color: #333 !important; /* Standard Textfarbe statt text-foreground */
          background: #fff !important; /* Standard Hintergrund */
          font-family: "Helvetica Neue", Helvetica, Arial, sans-serif !important;
          font-size: 14px !important;
          line-height: 1.42857143 !important;
          /* Höhe auf 100% setzen damit Container den vollen Platz nutzt */
          min-height: 100% !important;
          height: auto !important;
          flex: 1 1 auto !important;
        }

        /* Reset für alle Child-Elemente */
        .xsite-content-container * {
          color: inherit;
        }

        /* Links sollen Laravel-Farbe behalten */
        .xsite-content-container a {
          color: #0088cc;
          text-decoration: none;
        }

        .xsite-content-container a:hover {
          color: #005580;
          text-decoration: underline;
        }

        /* Bootstrap .row negative margins entfernen - verhindert Layout-Überlauf */
        .xsite-content-container .row {
          margin-left: 0 !important;
          margin-right: 0 !important;
        }

        /* Header Section color reset - soll von Child-Elementen erben */
        .xsite-content-container .content-header-media .header-section {
          color: inherit !important;
          height: auto !important;          /* ✅ Auto-grow ohne Mindesthöhe */
          position: relative !important;    /* ✅ Normale Positionierung */
          display: block !important;        /* ✅ Block-Element */
        }

        /* Header auto-grow - wächst mit Inhalt statt fixer Höhe */
        .xsite-content-container .content-header-media {
          height: auto !important;
          overflow: visible !important;
          position: relative !important;    /* ✅ Normale Positionierung */
        }

        /* Child-Elemente im Header: normale Positionierung */
        .xsite-content-container .content-header-media .header-section > * {
          position: relative !important;    /* ✅ Keine absolute Positionierung */
        }

        /* ====== Bootstrap 3 Grid System - NUR für .xsite-content-container ====== */

        /* Container */
        .xsite-content-container .container {
          margin-right: auto;
          margin-left: auto;
          padding-left: 15px;
          padding-right: 15px;
        }

        .xsite-content-container .container-fluid {
          margin-right: auto;
          margin-left: auto;
          padding-left: 15px;
          padding-right: 15px;
        }

        /* Row - OHNE negative Margins (wurde schon oben entfernt) */
        .xsite-content-container .row {
          margin-left: 0 !important;
          margin-right: 0 !important;
        }

        /* Clearfix für Row */
        .xsite-content-container .row:before,
        .xsite-content-container .row:after {
          content: " ";
          display: table;
        }

        .xsite-content-container .row:after {
          clear: both;
        }

        /* Alle Bootstrap Spalten - Basis Styles */
        .xsite-content-container .col-xs-1,
        .xsite-content-container .col-xs-2,
        .xsite-content-container .col-xs-3,
        .xsite-content-container .col-xs-4,
        .xsite-content-container .col-xs-5,
        .xsite-content-container .col-xs-6,
        .xsite-content-container .col-xs-7,
        .xsite-content-container .col-xs-8,
        .xsite-content-container .col-xs-9,
        .xsite-content-container .col-xs-10,
        .xsite-content-container .col-xs-11,
        .xsite-content-container .col-xs-12,
        .xsite-content-container .col-sm-1,
        .xsite-content-container .col-sm-2,
        .xsite-content-container .col-sm-3,
        .xsite-content-container .col-sm-4,
        .xsite-content-container .col-sm-5,
        .xsite-content-container .col-sm-6,
        .xsite-content-container .col-sm-7,
        .xsite-content-container .col-sm-8,
        .xsite-content-container .col-sm-9,
        .xsite-content-container .col-sm-10,
        .xsite-content-container .col-sm-11,
        .xsite-content-container .col-sm-12,
        .xsite-content-container .col-md-1,
        .xsite-content-container .col-md-2,
        .xsite-content-container .col-md-3,
        .xsite-content-container .col-md-4,
        .xsite-content-container .col-md-5,
        .xsite-content-container .col-md-6,
        .xsite-content-container .col-md-7,
        .xsite-content-container .col-md-8,
        .xsite-content-container .col-md-9,
        .xsite-content-container .col-md-10,
        .xsite-content-container .col-md-11,
        .xsite-content-container .col-md-12,
        .xsite-content-container .col-lg-1,
        .xsite-content-container .col-lg-2,
        .xsite-content-container .col-lg-3,
        .xsite-content-container .col-lg-4,
        .xsite-content-container .col-lg-5,
        .xsite-content-container .col-lg-6,
        .xsite-content-container .col-lg-7,
        .xsite-content-container .col-lg-8,
        .xsite-content-container .col-lg-9,
        .xsite-content-container .col-lg-10,
        .xsite-content-container .col-lg-11,
        .xsite-content-container .col-lg-12 {
          position: relative;
          min-height: 1px;
          padding-left: 15px;
          padding-right: 15px;
        }

        /* XS Grid (Mobile First - immer aktiv) */
        .xsite-content-container .col-xs-1,
        .xsite-content-container .col-xs-2,
        .xsite-content-container .col-xs-3,
        .xsite-content-container .col-xs-4,
        .xsite-content-container .col-xs-5,
        .xsite-content-container .col-xs-6,
        .xsite-content-container .col-xs-7,
        .xsite-content-container .col-xs-8,
        .xsite-content-container .col-xs-9,
        .xsite-content-container .col-xs-10,
        .xsite-content-container .col-xs-11,
        .xsite-content-container .col-xs-12 {
          float: left;
        }

        .xsite-content-container .col-xs-12 { width: 100%; }
        .xsite-content-container .col-xs-11 { width: 91.66666667%; }
        .xsite-content-container .col-xs-10 { width: 83.33333333%; }
        .xsite-content-container .col-xs-9 { width: 75%; }
        .xsite-content-container .col-xs-8 { width: 66.66666667%; }
        .xsite-content-container .col-xs-7 { width: 58.33333333%; }
        .xsite-content-container .col-xs-6 { width: 50%; }
        .xsite-content-container .col-xs-5 { width: 41.66666667%; }
        .xsite-content-container .col-xs-4 { width: 33.33333333%; }
        .xsite-content-container .col-xs-3 { width: 25%; }
        .xsite-content-container .col-xs-2 { width: 16.66666667%; }
        .xsite-content-container .col-xs-1 { width: 8.33333333%; }

        /* MD Grid (ab 992px Breite) */
        @media (min-width: 992px) {
          .xsite-content-container .col-md-1,
          .xsite-content-container .col-md-2,
          .xsite-content-container .col-md-3,
          .xsite-content-container .col-md-4,
          .xsite-content-container .col-md-5,
          .xsite-content-container .col-md-6,
          .xsite-content-container .col-md-7,
          .xsite-content-container .col-md-8,
          .xsite-content-container .col-md-9,
          .xsite-content-container .col-md-10,
          .xsite-content-container .col-md-11,
          .xsite-content-container .col-md-12 {
            float: left;
          }

          .xsite-content-container .col-md-12 { width: 100%; }
          .xsite-content-container .col-md-11 { width: 91.66666667%; }
          .xsite-content-container .col-md-10 { width: 83.33333333%; }
          .xsite-content-container .col-md-9 { width: 75%; }
          .xsite-content-container .col-md-8 { width: 66.66666667%; }
          .xsite-content-container .col-md-7 { width: 58.33333333%; }
          .xsite-content-container .col-md-6 { width: 50%; }
          .xsite-content-container .col-md-5 { width: 41.66666667%; }
          .xsite-content-container .col-md-4 { width: 33.33333333%; }
          .xsite-content-container .col-md-3 { width: 25%; }
          .xsite-content-container .col-md-2 { width: 16.66666667%; }
          .xsite-content-container .col-md-1 { width: 8.33333333%; }
        }

        /* Bootstrap 3 Fallback: .col-12 → .col-xs-12 (Bootstrap 3 hat keine .col-12) */
        .xsite-content-container .col-12 {
          position: relative;
          float: left;
          width: 100%;
          min-height: 1px;
          padding-left: 15px;
          padding-right: 15px;
        }
      `;
      document.head.appendChild(resetStyle);
      eclLog('[ExternalContentLoader] 🔧 Applied container CSS reset');
    }

    // 1. FontAwesome Pro 5.15.1 - Unterstützt far, fas, fal, fab (lokal)
    if (!document.getElementById('xsite-fontawesome-css')) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = '/vendor/fleitec/core/css/fonts/fontawesome-pro-5.15.1/css/all.css';
      link.id = 'xsite-fontawesome-css';
      document.head.appendChild(link);
      eclLog('[ExternalContentLoader] 📎 Loaded FontAwesome Pro 5.15.1 CSS');
    }

    // 2. Dediziertes Laravel Content CSS - Alle Styles in einem File, bereits scoped!
    // Enthält: Bootstrap 3 Components + DataTables + Custom Styles
    // Vorteil: Kein regex-Scoping nötig, viel schneller, kleinere Datei
    if (!document.getElementById('xsite-laravel-content-css')) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = '/css/xsite-laravel-content.css';
      link.id = 'xsite-laravel-content-css';
      document.head.appendChild(link);
      eclLog('[ExternalContentLoader] ✅ Loaded dedicated Laravel content CSS');
    }

    // 3. jQuery UI CSS - NUR für .xsite-content-container gescoped!
    if (!document.getElementById('xsite-jquery-ui-scoped-css')) {
      await loadAndScopeCSS(
        '/vendor/fleitec/core/css/jquery-ui.min.css',
        'xsite-jquery-ui-scoped-css',
        'jQuery UI'
      );
    }

    // 4. DateTimePicker CSS - NUR für .xsite-content-container gescoped!
    if (!document.getElementById('xsite-datetimepicker-scoped-css')) {
      await loadAndScopeCSS(
        '/vendor/jquery/jquery.datetimepicker.min.css',
        'xsite-datetimepicker-scoped-css',
        'DateTimePicker'
      );
    }

    // 5. Wasserveik Theme CSS - PRE-SCOPED (no runtime regex!)
    // Alle Selektoren sind bereits mit .xsite-content-container prefixed
    // Datei wurde mit scope-css-v2.js erstellt (siehe /tmp/scope-css-v2.js)
    const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
    if ((hostname === 'office.truu.com' || hostname === 'react.truu.com') &&
        !document.getElementById('xsite-wasserveik-scoped-css')) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = '/css/wasserveik-scoped.css';
      link.id = 'xsite-wasserveik-scoped-css';
      document.head.appendChild(link);
      eclLog('[ExternalContentLoader] ✅ Loaded pre-scoped Wasserveik Theme CSS');
    }

    // 6. Plugins CSS - PRE-SCOPED (Glyphicons, DataTables, FontAwesome 4, etc.)
    // Enthält: Glyphicons PRO, DataTables, Datepicker, Select2, etc.
    if (!document.getElementById('xsite-plugins-scoped-css')) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = '/css/plugins-scoped.css';
      link.id = 'xsite-plugins-scoped-css';
      document.head.appendChild(link);
      eclLog('[ExternalContentLoader] ✅ Loaded pre-scoped Plugins CSS');
    }

    // 7. App CSS - PRE-SCOPED (Bootstrap 4)
    // Bootstrap 4 Utilities und Components
    if (!document.getElementById('xsite-app-scoped-css')) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = '/css/app-scoped.css';
      link.id = 'xsite-app-scoped-css';
      document.head.appendChild(link);
      eclLog('[ExternalContentLoader] ✅ Loaded pre-scoped App/Bootstrap 4 CSS');
    }

    // 8. DataTables Base CSS - PRE-SCOPED
    // DataTables wrapper, filter, length, paginate, info styles
    if (!document.getElementById('xsite-datatables-scoped-css')) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = '/css/datatables-scoped.css';
      link.id = 'xsite-datatables-scoped-css';
      document.head.appendChild(link);
      eclLog('[ExternalContentLoader] ✅ Loaded pre-scoped DataTables CSS');
    }

    setCssLoaded(true);
    eclLog('[ExternalContentLoader] ✅ All scoped CSS loaded!');
  };
  
  // Load external scripts after content is rendered
  const loadExternalScripts = async () => {
    if (typeof window === 'undefined') return;
    
    // eclLog('[ExternalContentLoader] 📜 Loading external scripts...');
    
    // Sequential script loading - Using CDN for DataTables (local bundle is corrupted)
    const scripts = [
      // jQuery 3.4.1 (CDN)
      { src: 'https://code.jquery.com/jquery-3.4.1.min.js', id: 'xsite-jquery' },
      // DataTables Complete Bundle (CDN - includes core + bootstrap integration)
      { src: 'https://cdn.datatables.net/v/bs/jszip-2.5.0/dt-1.13.1/b-2.3.3/b-html5-2.3.3/b-print-2.3.3/datatables.min.js', id: 'xsite-datatables' },
      // DataTables Editor (Local - Commercial license)
      { src: '/vendor/fleitec/core/js/dataTables.editor.min.js', id: 'xsite-datatables-editor' },
      // Bootstrap 3 (Local)
      { src: '/vendor/fleitec/core/js/bootstrap.min.js', id: 'xsite-bootstrap-js' },
      // DateTimePicker (Local)
      { src: '/vendor/jquery/jquery.datetimepicker.full.min.js', id: 'xsite-datetimepicker' },
      // Additional Laravel Dependencies (from invoice.js requirements)
      { src: '/vendor/fleitec/core/js/json2.js', id: 'xsite-json2' },
      // sw.js REMOVED - tries to register service-worker.js
      { src: '/vendor/fleitec/core/js/SimpleAjaxUploader.js', id: 'xsite-simple-ajax-uploader' },
      { src: '/vendor/fleitec/core/js/uploadField.js', id: 'xsite-upload-field' },
      { src: '/vendor/fleitec/core/js/ticket.js', id: 'xsite-ticket' },
      { src: '/vendor/fleitec/core/js/plugins.js', id: 'xsite-plugins' },
      { src: '/vendor/fleitec/core/js/plugins_xtra.js', id: 'xsite-plugins-xtra' },
      // CKEditor (Local)
      { src: '/vendor/fleitec/core/js/ckeditor.js', id: 'xsite-ckeditor' },
      // CKEditor jQuery Adapter (Local - must be loaded AFTER CKEditor core)
      { src: '/vendor/fleitec/core/js/ckeditor/adapters/jquery.js', id: 'xsite-ckeditor-jquery' },
      // TinyMCE (Local)
      { src: '/vendor/fleitec/core/js/tinymce.min.js', id: 'xsite-tinymce' },
      // Custom scripts (Local)
      { src: '/vendor/fleitec/core/js/custom.js', id: 'xsite-custom' },
      { src: '/vendor/fleitec/core/js/ajax.js', id: 'xsite-ajax' },
      { src: '/vendor/fleitec/core/js/app.js', id: 'xsite-app' }
    ];
    
    // Load scripts sequentially
    for (const script of scripts) {
      if (!document.getElementById(script.id)) {
        eclLog(`[ExternalContentLoader] 📥 Loading script: ${script.id} from ${script.src}`);
        await new Promise((resolve) => {
          const scriptEl = document.createElement('script');
          scriptEl.src = script.src;
          scriptEl.id = script.id;
          scriptEl.onload = () => {
            eclLog(`[ExternalContentLoader] ✅ Loaded script: ${script.id}`);
            resolve();
          };
          scriptEl.onerror = (error) => {
            console.error(`[ExternalContentLoader] ❌ Failed to load script: ${script.id}`, error);
            resolve(); // Continue anyway
          };
          document.body.appendChild(scriptEl);
        });
      } else {
        eclLog(`[ExternalContentLoader] ⏭️ Script already loaded: ${script.id}`);
      }
    }
  };
  
  // Initialize scripts and widgets after content update
  const initializeScripts = async () => {
    if (!contentRef.current || typeof window === 'undefined') return;

    eclLog('[ExternalContentLoader] 🔧 Initializing scripts and widgets...');

    // Wait for DataTables to be fully loaded and registered on jQuery
    eclLog('[ExternalContentLoader] ⏳ Waiting for DataTables to be ready...');
    let attempts = 0;
    const maxAttempts = 50; // 5 seconds total (50 * 100ms)

    while (attempts < maxAttempts) {
      if (window.$ && window.$.fn && window.$.fn.DataTable && window.$.fn.dataTable) {
        eclLog('[ExternalContentLoader] ✅ DataTables is ready!', {
          'jQuery': !!window.$,
          '$.fn': !!window.$.fn,
          '$.fn.DataTable': !!window.$.fn.DataTable,
          '$.fn.dataTable': !!window.$.fn.dataTable,
          'DataTable.version': window.$.fn.DataTable ? window.$.fn.DataTable.version : 'N/A'
        });
        break;
      }

      eclLog(`[ExternalContentLoader] ⏳ DataTables not ready yet, attempt ${attempts + 1}/${maxAttempts}`, {
        'jQuery': !!window.$,
        '$.fn': !!(window.$ && window.$.fn),
        '$.fn.DataTable': !!(window.$ && window.$.fn && window.$.fn.DataTable),
        '$.fn.dataTable': !!(window.$ && window.$.fn && window.$.fn.dataTable)
      });

      await new Promise(resolve => setTimeout(resolve, 100));
      attempts++;
    }

    if (attempts >= maxAttempts) {
      console.error('[ExternalContentLoader] ❌ Timeout waiting for DataTables to load!');
    }

    // Additional safety wait
    await new Promise(resolve => setTimeout(resolve, 200));

    // Initialize Laravel App
    if (window.App && typeof window.App.init_ajax === 'function') {
      try {
        // eclLog('[ExternalContentLoader] 🚀 Initializing App.init_ajax()');
        window.App.init_ajax();
      } catch (e) {
        console.error('[ExternalContentLoader] ❌ Error initializing App.init_ajax:', e);
      }
    }

    // Initialize jQuery plugins
    if (window.$ && contentRef.current) {
      const $container = window.$(contentRef.current);

      // eclLog('[ExternalContentLoader] 🔍 Searching for jQuery plugins to initialize...');

      // Initialize DataTables
      const $dataTables = $container.find('.datatable, .dataTable, table[data-datatable]');
      // eclLog(`[ExternalContentLoader] 📊 Found ${$dataTables.length} DataTable(s) to initialize`);
      $dataTables.each(function(index) {
        try {
          const $table = window.$(this);
          if (!window.$.fn.DataTable.isDataTable($table)) {
            eclLog(`[ExternalContentLoader] ⚡ Initializing DataTable ${index + 1}...`);
            $table.DataTable({
              // Common options for Laravel DataTables
              language: {
                url: '//cdn.datatables.net/plug-ins/1.10.24/i18n/German.json'
              },
              responsive: true,
              autoWidth: false,
              processing: true
            });
            eclLog(`[ExternalContentLoader] ✅ DataTable ${index + 1} initialized`);
          }
        } catch (e) {
          console.error(`[ExternalContentLoader] ❌ Error initializing DataTable ${index + 1}:`, e);
        }
      });

      // Initialize date pickers
      const $datePickers = $container.find('.datepicker');
      // eclLog(`[ExternalContentLoader] 📅 Found ${$datePickers.length} datepicker(s) to initialize`);
      $datePickers.each(function(index) {
        try {
          const $picker = window.$(this);
          if (!$picker.data('DateTimePicker')) {
            eclLog(`[ExternalContentLoader] ⚡ Initializing datepicker ${index + 1}...`);
            $picker.datetimepicker({
              format: 'DD.MM.YYYY',
              locale: 'de'
            });
            eclLog(`[ExternalContentLoader] ✅ Datepicker ${index + 1} initialized`);
          }
        } catch (e) {
          console.error(`[ExternalContentLoader] ❌ Error initializing datepicker ${index + 1}:`, e);
        }
      });

      // Initialize Select2 if exists
      if (window.$.fn.select2) {
        const $select2 = $container.find('select.select2');
        // eclLog(`[ExternalContentLoader] 🎯 Found ${$select2.length} Select2 element(s) to initialize`);
        $select2.each(function(index) {
          try {
            const $select = window.$(this);
            if (!$select.data('select2')) {
              eclLog(`[ExternalContentLoader] ⚡ Initializing Select2 ${index + 1}...`);
              $select.select2();
              eclLog(`[ExternalContentLoader] ✅ Select2 ${index + 1} initialized`);
            }
          } catch (e) {
            console.error(`[ExternalContentLoader] ❌ Error initializing Select2 ${index + 1}:`, e);
          }
        });
      }

      // Initialize tooltips
      if (window.$.fn.tooltip) {
        const $tooltips = $container.find('[data-toggle="tooltip"]');
        // eclLog(`[ExternalContentLoader] 💬 Found ${$tooltips.length} tooltip(s) to initialize`);
        try {
          $tooltips.tooltip();
          // eclLog(`[ExternalContentLoader] ✅ Tooltips initialized`);
        } catch (e) {
          console.error('[ExternalContentLoader] ❌ Error initializing tooltips:', e);
        }
      }

      // Initialize popovers
      if (window.$.fn.popover) {
        const $popovers = $container.find('[data-toggle="popover"]');
        // eclLog(`[ExternalContentLoader] 💭 Found ${$popovers.length} popover(s) to initialize`);
        try {
          $popovers.popover();
          // eclLog(`[ExternalContentLoader] ✅ Popovers initialized`);
        } catch (e) {
          console.error('[ExternalContentLoader] ❌ Error initializing popovers:', e);
        }
      }

      // eclLog('[ExternalContentLoader] ✅ All jQuery plugins initialized');

      // Initialize LazyXoffi Widgets (Dashboard widgets with IntersectionObserver)
      // Retry mechanism: Wait for window.observer to be available
      const initializeLazyWidgets = async () => {
        eclLog('[ExternalContentLoader] 🎯 Initializing LazyXoffi widgets...');
        const $lazyWidgets = $container.find('.lazyxoffi');
        eclLog(`[ExternalContentLoader] 📊 Found ${$lazyWidgets.length} LazyXoffi widget(s)`);

        if ($lazyWidgets.length === 0) {
          eclLog('[ExternalContentLoader] ⏭️ No LazyXoffi widgets found, skipping');
          return;
        }

        // Wait for observer to be available (max 5 seconds)
        let observerAttempts = 0;
        const maxObserverAttempts = 50;

        while (!window.observer && observerAttempts < maxObserverAttempts) {
          eclLog(`[ExternalContentLoader] ⏳ Waiting for IntersectionObserver... attempt ${observerAttempts + 1}/${maxObserverAttempts}`);
          await new Promise(resolve => setTimeout(resolve, 100));
          observerAttempts++;
        }

        if (!window.observer) {
          console.error('[ExternalContentLoader] ❌ IntersectionObserver not available after 5s timeout!');
          return;
        }

        eclLog('[ExternalContentLoader] ✅ IntersectionObserver is ready, initializing widgets...');

        // Helper: Check if element is in viewport
        const isInViewport = (element) => {
          const rect = element.getBoundingClientRect();
          return (
            rect.top >= 0 &&
            rect.left >= 0 &&
            rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
            rect.right <= (window.innerWidth || document.documentElement.clientWidth)
          );
        };

        // Helper: Manually load widget (trigger the same logic as IntersectionObserver)
        const loadWidgetManually = async ($widget, widgetUrl) => {
          eclLog('[ExternalContentLoader] 🚀 Loading widget immediately (in viewport):', widgetUrl);

          const $content = $widget.find('.content');
          eclLog('[ExternalContentLoader] 🔍 DEBUG: Found .content element?', $content.length > 0, 'Length:', $content.length);

          if ($content.length === 0) {
            console.error('[ExternalContentLoader] ❌ No .content element found in widget! Widget HTML:', $widget[0]?.outerHTML?.substring(0, 200));
            return;
          }

          // Show loading spinner
          $content.html('<div class="spinner spinner-primary spinner-lg mr-15"></div>');

          try {
            const response = await window.$.ajax({
              url: widgetUrl,
              type: 'GET',
              dataType: 'html'
            });

            eclLog('[ExternalContentLoader] 🔍 DEBUG: AJAX Response received, length:', response?.length || 0);
            eclLog('[ExternalContentLoader] 🔍 DEBUG: Response preview (first 200 chars):', response?.substring(0, 200));

            // Store in global cache
            if (window.loadedWidgets && window.widgetContent) {
              window.loadedWidgets.add(widgetUrl);
              window.widgetContent.set(widgetUrl, response);
            }

            // Update widget
            $content.html(response);
            eclLog('[ExternalContentLoader] 🔍 DEBUG: HTML inserted into .content, new children count:', $content.children().length);

            $widget.addClass('widgetLoaded');
            $widget.attr('data-widget-loaded', 'true');

            eclLog('[ExternalContentLoader] ✅ Widget loaded successfully:', widgetUrl);
          } catch (error) {
            console.error('[ExternalContentLoader] ❌ Failed to load widget:', widgetUrl, error);
            $content.html('<div class="alert alert-danger">Fehler beim Laden des Widgets</div>');
          }
        };

        // Initialize each widget
        $lazyWidgets.each(function() {
          const $widget = window.$(this);
          const widgetUrl = $widget.data('url');
          const hasWidgetLoadedClass = $widget.hasClass('widgetLoaded');
          const hasDataAttribute = $widget.attr('data-widget-loaded') === 'true';
          const hasContent = $widget.find('.content').children().length > 0;
          const isInGlobalCache = window.loadedWidgets && window.loadedWidgets.has(widgetUrl);

          // Restore from global cache if available
          if (isInGlobalCache && !hasContent && window.widgetContent) {
            eclLog('[ExternalContentLoader] 🔄 Restoring widget from global cache:', widgetUrl);
            const cachedContent = window.widgetContent.get(widgetUrl);
            if (cachedContent) {
              $widget.find('.content').html(cachedContent);
              $widget.addClass('widgetLoaded');
              $widget.attr('data-widget-loaded', 'true');
            }
            return; // Skip to next widget
          }

          // Check if widget needs loading - ONLY check hasContent and cache, ignore hasClass/hasData
          // (old Laravel IntersectionObserver may have set class/data but didn't load content properly)
          if (!hasContent && !isInGlobalCache) {
            // Check if widget is already visible in viewport
            if (isInViewport(this)) {
              // Load immediately without waiting for scroll
              eclLog('[ExternalContentLoader] 👁️ Widget is in viewport, loading immediately:', widgetUrl);
              loadWidgetManually($widget, widgetUrl);
            } else {
              // Add to IntersectionObserver for lazy loading when scrolled into view
              eclLog('[ExternalContentLoader] 👀 Widget not in viewport, adding to IntersectionObserver:', widgetUrl);
              window.observer.observe(this);
            }
          } else {
            eclLog('[ExternalContentLoader] ⏭️ Widget already loaded, skipping:', {
              url: widgetUrl,
              hasClass: hasWidgetLoadedClass,
              hasData: hasDataAttribute,
              hasContent: hasContent,
              cached: isInGlobalCache
            });
          }
        });

        eclLog('[ExternalContentLoader] ✅ LazyXoffi widget initialization complete!');
      };

      // Run widget initialization asynchronously (don't block other initialization)
      initializeLazyWidgets().catch(error => {
        console.error('[ExternalContentLoader] ❌ Error initializing LazyXoffi widgets:', error);
      });
    } else {
      if (!window.$) {
        console.warn('[ExternalContentLoader] ⚠️ jQuery not available - cannot initialize plugins');
      }
      if (!contentRef.current) {
        console.warn('[ExternalContentLoader] ⚠️ contentRef not available - cannot initialize plugins');
      }
    }

    // Fetch CSRF token if needed
    await fetchCSRFToken();

    // eclLog('[ExternalContentLoader] ✅ Script initialization complete!');
  };
  
  // Helper: Extract and execute script tags from HTML
  const executeScriptsFromHTML = (htmlString) => {
    // eclLog('[ExternalContentLoader] 🔍 DEBUG: Extracting scripts from HTML...');

    // Find all <script> tags in the HTML (both inline and external)
    const scriptRegex = /<script\b([^>]*)>([\s\S]*?)<\/script>/gi;
    const inlineScripts = [];
    const externalScripts = [];
    let match;

    while ((match = scriptRegex.exec(htmlString)) !== null) {
      const attributes = match[1]; // Script tag attributes
      const content = match[2]; // Script content

      // Check if it's an external script (has src attribute)
      const srcMatch = attributes.match(/src=["']([^"']+)["']/);

      if (srcMatch) {
        externalScripts.push(srcMatch[1]); // External script URL
      } else if (content.trim()) {
        inlineScripts.push(content); // Inline script content
      }
    }

    // eclLog(`[ExternalContentLoader] 📜 Found ${externalScripts.length} external script(s) and ${inlineScripts.length} inline script(s)`);

    // Load external scripts first (sequentially)
    const loadExternalScriptsFromHTML = async () => {
      for (let i = 0; i < externalScripts.length; i++) {
        const src = externalScripts[i];
        try {
          // Skip if already loaded
          if (document.querySelector(`script[src="${src}"]`)) {
            eclLog(`[ExternalContentLoader] ⏭️ External script already loaded: ${src}`);
            continue;
          }

          // eclLog(`[ExternalContentLoader] ⚡ Loading external script ${i + 1}/${externalScripts.length}: ${src}`);
          await new Promise((resolve, reject) => {
            const scriptEl = document.createElement('script');
            scriptEl.src = src;
            scriptEl.onload = resolve;
            scriptEl.onerror = () => {
              console.warn(`[ExternalContentLoader] ⚠️ Failed to load external script: ${src}`);
              resolve(); // Continue anyway
            };
            document.body.appendChild(scriptEl);
          });
          // eclLog(`[ExternalContentLoader] ✅ External script ${i + 1} loaded successfully`);
        } catch (e) {
          console.error(`[ExternalContentLoader] ❌ Error loading external script ${i + 1}:`, e);
        }
      }
    };

    // Execute inline scripts
    const executeInlineScripts = () => {
      inlineScripts.forEach((scriptContent, index) => {
        try {
          // eclLog(`[ExternalContentLoader] ⚡ Executing inline script ${index + 1}/${inlineScripts.length}`);
          // Create a new script element and execute it
          const scriptEl = document.createElement('script');

          // ✅ FIX: Wrap script content in try/catch to handle missing function references
          // This prevents one broken script from breaking all subsequent scripts
          scriptEl.textContent = `
            try {
              ${scriptContent}
            } catch (e) {
              if (e instanceof ReferenceError) {
                console.warn('[Laravel Script] Missing function reference:', e.message);
                console.warn('  → Consider adding a stub for this function in ExternalContentLoader');
              } else {
                console.error('[Laravel Script] Execution error:', e);
                throw e; // Re-throw non-reference errors
              }
            }
          `;

          document.body.appendChild(scriptEl);
          document.body.removeChild(scriptEl); // Clean up
          // eclLog(`[ExternalContentLoader] ✅ Inline script ${index + 1} executed successfully`);
        } catch (e) {
          console.error(`[ExternalContentLoader] ❌ Error executing inline script ${index + 1}:`, e);
        }
      });
    };

    // Load external scripts first, then execute inline scripts
    loadExternalScriptsFromHTML().then(() => {
      // eclLog('[ExternalContentLoader] 🔍 DEBUG: All external scripts from HTML loaded, executing inline scripts...');
      executeInlineScripts();
      // eclLog('[ExternalContentLoader] ✅ All scripts from HTML executed!');
    });
  };

  // Load content function
  const loadContent = async (url) => {
    // eclLog('[ExternalContentLoader] 🔍 DEBUG: loadContent called with url:', url);

    if (!url) {
      // eclLog('[ExternalContentLoader] 🔍 DEBUG: URL is empty, returning');
      return;
    }

    // Prevent duplicate loads
    if (loadingRef.current) {
      // eclLog('[ExternalContentLoader] ⏸️ Already loading, skipping...');
      return;
    }

    // Convert /xsite/ URLs to /api/xsite/ for backend
    let apiUrl = url;
    if (url.startsWith('/xsite/')) {
      apiUrl = url.replace(/^\/xsite\//, '/api/xsite/');
    }
    // eclLog('[ExternalContentLoader] 🔍 DEBUG: Converted URL to apiUrl:', apiUrl);

    // Skip if already loaded this URL
    if (currentUrl === apiUrl) {
      // eclLog('[ExternalContentLoader] ✅ Content already loaded for:', apiUrl);
      return;
    }

    // eclLog('[ExternalContentLoader] 🚀 Loading content from:', apiUrl);
    loadingRef.current = true;
    setLoading(true);
    setError(null);

    try {
      // Load CSS first
      // eclLog('[ExternalContentLoader] 🔍 DEBUG: Loading CSS...');
      await loadExternalCSS();
      // eclLog('[ExternalContentLoader] 🔍 DEBUG: CSS loaded successfully');

      // Fetch content with timeout
      const authToken = localStorage.getItem('auth_token') || localStorage.getItem('token');
      eclLog('[ExternalContentLoader] 🔍 DEBUG: Auth token exists:', !!authToken);
      eclLog('[ExternalContentLoader] 🔍 DEBUG: Auth token (first 20 chars):', authToken ? authToken.substring(0, 20) + '...' : 'none');
      eclLog('[ExternalContentLoader] 🔍 DEBUG: Starting fetch request to:', apiUrl);

      // Create abort controller for timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => {
        console.error('[ExternalContentLoader] ⏱️ Fetch timeout after 10 seconds!');
        controller.abort();
      }, 10000); // 10 second timeout

      let response;
      try {
        response = await fetch(apiUrl, {
          headers: {
            'Authorization': authToken ? `Bearer ${authToken}` : '',
            'Accept': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          signal: controller.signal
        });
        clearTimeout(timeoutId);
        eclLog('[ExternalContentLoader] 🔍 DEBUG: Fetch completed, response.ok:', response.ok, 'status:', response.status);
      } catch (fetchError) {
        clearTimeout(timeoutId);
        console.error('[ExternalContentLoader] ❌ Fetch error:', fetchError);
        console.error('[ExternalContentLoader] ❌ Fetch error name:', fetchError.name);
        console.error('[ExternalContentLoader] ❌ Fetch error message:', fetchError.message);
        throw new Error(`Fetch failed: ${fetchError.message}`);
      }

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      // Backend returns JSON - support both old and new format
      // Old: {success: true, data: "<html>..."}
      // New: {success: true, content: "<html>...", scripts: "...", csrf_token: "..."}
      // eclLog('[ExternalContentLoader] 🔍 DEBUG: Parsing JSON response...');
      const jsonData = await response.json();
      eclLog('[ExternalContentLoader] 📋 JSON Response:', {
        success: jsonData.success,
        hasContent: !!jsonData.content,
        hasData: !!jsonData.data,
        hasScripts: !!jsonData.scripts,
        hasCsrfToken: !!jsonData.csrf_token
      });

      if (!jsonData.success) {
        throw new Error(jsonData.error || 'Failed to load content');
      }

      // Support both formats: content (new) or data (old)
      let htmlContent = jsonData.content || jsonData.data || '';

      // If we have scripts, append them to content
      if (jsonData.scripts) {
        eclLog('[ExternalContentLoader] 📜 Appending scripts to content');
        htmlContent += '\n' + jsonData.scripts;
      }

      // Store CSRF token if provided
      if (jsonData.csrf_token) {
        eclLog('[ExternalContentLoader] 🛡️ Storing CSRF token from response');
        window.csrfToken = jsonData.csrf_token;

        // Update meta tag
        let metaTag = document.querySelector('meta[name="csrf-token"]');
        if (!metaTag) {
          metaTag = document.createElement('meta');
          metaTag.name = 'csrf-token';
          document.head.appendChild(metaTag);
        }
        metaTag.content = jsonData.csrf_token;

        updateJQueryAjaxCSRF();
      }

      // eclLog('[ExternalContentLoader] 🔍 DEBUG: Extracted htmlContent, length:', htmlContent.length);

      if (!htmlContent) {
        throw new Error('No HTML content returned from server');
      }

      eclLog('[ExternalContentLoader] ✅ Received HTML content, length:', htmlContent.length);
      eclLog('[ExternalContentLoader] 🔍 DEBUG: Calling setLaravelHtmlContent...');

      // Set content via React state (this will trigger re-render)
      setLaravelHtmlContent(htmlContent);
      eclLog('[ExternalContentLoader] 🔍 DEBUG: setLaravelHtmlContent called');

      setCurrentUrl(apiUrl);
      // eclLog('[ExternalContentLoader] 🔍 DEBUG: setCurrentUrl called');

      // Note: Inline scripts will be executed AFTER external scripts load
      // (see checkRefAndInitialize in useEffect below)

      // DON'T update browser URL if we're already on /xsite/ route
      // (XSitePage handles that and updating would cause a loop!)
      const isAlreadyXSiteRoute = window.location.pathname.startsWith('/xsite/');
      if (!isAlreadyXSiteRoute) {
        // Only update URL if loaded via custom event (not via XSitePage route)
        const pathMatch = apiUrl.match(/\/api\/xsite\/\d+\/(.+?)(\?.*)?$/);
        if (pathMatch) {
          const path = pathMatch[1];
          const queryString = pathMatch[2] || '';
          const newUrl = `/xsite/10/${path}${queryString}`;
          // eclLog('[ExternalContentLoader] 📍 Updating browser URL to:', newUrl);
          window.history.pushState({ xsiteUrl: apiUrl }, '', newUrl);
        }
      } else {
        // eclLog('[ExternalContentLoader] ⏭️ Skipping URL update (already on /xsite/ route)');
      }

    } catch (err) {
      console.error('[ExternalContentLoader] ❌ Error loading content:', err);
      // eclLog('[ExternalContentLoader] 🔍 DEBUG: Error details:', {
      //   message: err.message,
      //   stack: err.stack,
      //   name: err.name
      // });
      setError(err.message);
    } finally {
      // eclLog('[ExternalContentLoader] 🔍 DEBUG: Finally block - cleaning up, loading:', false);
      setLoading(false);
      loadingRef.current = false;
    }
  };
  
  // Clear content function
  const clearContent = () => {
    // eclLog('[ExternalContentLoader] 🧹 Clearing content');
    setLaravelHtmlContent(null);
    setCurrentUrl(null);
    setError(null);

    // Reset browser URL
    if (typeof window !== 'undefined' && currentUrl) {
      window.history.pushState({}, '', '/');
    }
  };

  // 🔥 FIX: Store latest loadContent in ref so event listener can always access it
  useEffect(() => {
    loadContentRef.current = loadContent;
    // eclLog('[ExternalContentLoader] 🔍 DEBUG: Updated loadContentRef with latest loadContent function');
  }, [loadContent]);

  // Debug: Track laravelHtmlContent state changes
  useEffect(() => {
    // eclLog('[ExternalContentLoader] 🔍 DEBUG: laravelHtmlContent state changed:', {
    //   hasContent: !!laravelHtmlContent,
    //   length: laravelHtmlContent?.length || 0,
    //   firstChars: laravelHtmlContent?.substring(0, 100) || 'null'
    // });
  }, [laravelHtmlContent]);

  // Effect to initialize scripts after Laravel content is rendered
  useEffect(() => {
    if (!laravelHtmlContent) {
      eclLog('[ExternalContentLoader] ⚠️ No content to initialize scripts for');
      return;
    }

    eclLog('[ExternalContentLoader] ✅ Content set, waiting for DOM to be ready...');

    // Wait for contentRef to be populated (DOM render complete)
    const checkRefAndInitialize = async () => {
      let attempts = 0;
      const maxAttempts = 20; // 2 seconds total (20 * 100ms)

      while (attempts < maxAttempts) {
        if (contentRef.current) {
          eclLog('[ExternalContentLoader] ✅ DOM is ready! Starting script initialization...');

          // Give browser one more tick to fully render
          await new Promise(resolve => setTimeout(resolve, 100));

          eclLog('[ExternalContentLoader] 🔍 DEBUG: Loading external scripts...');
          await loadExternalScripts();
          eclLog('[ExternalContentLoader] 🔍 DEBUG: External scripts loaded!');

          eclLog('[ExternalContentLoader] 🔍 DEBUG: Initializing jQuery plugins and LazyXoffi widgets...');
          await initializeScripts();
          eclLog('[ExternalContentLoader] 🔍 DEBUG: jQuery plugins and widgets initialized!');

          // 🔥 CRITICAL: Execute inline scripts LAST (after jQuery & plugins are ready)
          eclLog('[ExternalContentLoader] 🔍 DEBUG: Executing inline scripts from HTML...');
          executeScriptsFromHTML(laravelHtmlContent);
          eclLog('[ExternalContentLoader] 🔍 DEBUG: Inline scripts executed!');

          eclLog('[ExternalContentLoader] ✅ All scripts loaded and initialized!');
          return;
        }

        eclLog(`[ExternalContentLoader] ⏳ Waiting for DOM... attempt ${attempts + 1}/${maxAttempts}`);
        await new Promise(resolve => setTimeout(resolve, 100));
        attempts++;
      }

      console.error('[ExternalContentLoader] ❌ Timeout waiting for contentRef - DOM not rendered');
    };

    checkRefAndInitialize();
  }, [laravelHtmlContent]); // Only watch laravelHtmlContent - contentRef.current doesn't trigger re-renders!
  
  // Set up event listeners
  useEffect(() => {
    if (typeof window === 'undefined') return;

    eclLog('[ExternalContentLoader] 🎯 Setting up event listeners for loadExternalContent and clearExternalContent');

    const handleLoadEvent = (event) => {
      eclLog('[ExternalContentLoader] 🔍 DEBUG: loadExternalContent event received:', event.detail);
      if (event.detail && event.detail.url && loadContentRef.current) {
        eclLog('[ExternalContentLoader] 🔍 DEBUG: Calling loadContent via ref with URL:', event.detail.url);
        // 🔥 FIX: Call via ref to get latest version of loadContent (fixes stale closure bug)
        loadContentRef.current(event.detail.url);
      } else {
        eclLog('[ExternalContentLoader] 🔍 DEBUG: Event has no URL or loadContentRef not ready');
      }
    };

    const handleClearEvent = () => {
      clearContent();
    };

    window.addEventListener('loadExternalContent', handleLoadEvent);
    window.addEventListener('clearExternalContent', handleClearEvent);
    eclLog('[ExternalContentLoader] ✅ Event listeners registered successfully!');

    // Debug functions
    window.testExternalContentLoader = () => {
      // eclLog('[ExternalContentLoader] 🧪 Testing...');
      if (loadContentRef.current) {
        loadContentRef.current('/api/xsite/10/site/show/dashboard');
      }
    };

    return () => {
      window.removeEventListener('loadExternalContent', handleLoadEvent);
      window.removeEventListener('clearExternalContent', handleClearEvent);
    };
  }, []); // Empty dependencies OK now - we use ref to access latest loadContent
  
  // Context value
  const contextValue = {
    loading,
    error,
    cssLoaded,
    loadContent,
    clearContent,
    content: laravelHtmlContent,
    hasContent: !!laravelHtmlContent
  };
  
  // Debug: Log render decision
  eclLog('[ExternalContentLoader] 🔍 DEBUG: Rendering, laravelHtmlContent:', !!laravelHtmlContent, 'loading:', loading);

  return (
    <ExternalContentContext.Provider value={contextValue}>
      {/* Conditionally render either React children or Laravel content */}
      {laravelHtmlContent ? (
        <div
          ref={contentRef}
          className="external-content-container xsite-content-container"
          style={{ minHeight: '100%', height: 'auto', flex: '1 1 auto' }}
          dangerouslySetInnerHTML={{ __html: laravelHtmlContent }}
        />
      ) : (
        <>
          {children}
          {loading && (
            <div style={{
              position: 'fixed',
              top: '50px',
              right: '20px',
              zIndex: 10000
            }}>
              <div style={{
                width: '24px',
                height: '24px',
                border: '2px solid rgba(0, 0, 0, 0.1)',
                borderTop: '2px solid #3b82f6',
                borderRadius: '50%',
                animation: 'spin 0.8s linear infinite'
              }} />
              <style>{`
                @keyframes spin {
                  0% { transform: rotate(0deg); }
                  100% { transform: rotate(360deg); }
                }
              `}</style>
            </div>
          )}
        </>
      )}
    </ExternalContentContext.Provider>
  );
}

// CSRF Token fetching (simplified)
const fetchCSRFToken = async () => {
  eclLog('[ExternalContentLoader] 🛡️ Fetching CSRF token...');
  
  // Check if already in DOM
  const existingToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') ||
                       document.querySelector('input[name="_token"]')?.value;
  
  if (existingToken) {
    window.csrfToken = existingToken;
    updateJQueryAjaxCSRF();
    return existingToken;
  }
  
  // Fetch from backend
  try {
    const authToken = localStorage.getItem('auth_token') || localStorage.getItem('token');
    if (!authToken) return null;
    
    const response = await fetch('/api/xsite/10/curlsite/backend/', {
      headers: {
        'Authorization': `Bearer ${authToken}`,
        'Accept': 'application/json'
      }
    });
    
    if (response.ok) {
      const data = await response.json();
      
      if (data.success && data.csrf_token) {
        window.csrfToken = data.csrf_token;
        
        // Inject into DOM
        if (!document.querySelector('meta[name="csrf-token"]')) {
          const meta = document.createElement('meta');
          meta.name = 'csrf-token';
          meta.content = data.csrf_token;
          document.head.appendChild(meta);
        }
        
        updateJQueryAjaxCSRF();
        return data.csrf_token;
      }
    }
  } catch (error) {
    console.error('[ExternalContentLoader] ❌ Error fetching CSRF token:', error);
  }
  
  return null;
};

// Update jQuery AJAX setup with CSRF and Bearer tokens
const updateJQueryAjaxCSRF = () => {
  if (window.$ && window.$.ajaxSetup) {
    const csrfToken = window.csrfToken || document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');

    // Get Bearer token from localStorage or cookies
    const authToken = localStorage.getItem('auth_token') ||
                     localStorage.getItem('token') ||
                     document.cookie.match(/auth-token=([^;]+)/)?.[1] ||
                     document.cookie.match(/token=([^;]+)/)?.[1];

    // 🔥 CRITICAL: URL Rewriting PreFilter
    // Rewrites all /de/web/ and /de/file/ requests to go through /api/xsite/ proxy
    // This ensures Laravel token is automatically added by the proxy
    if (window.$.ajaxPrefilter && !window.__xsitePrefilterInstalled) {
      window.$.ajaxPrefilter(function(options, originalOptions, jqXHR) {
        // Only rewrite URLs that start with /de/web/ or /de/file/
        if (options.url && (options.url.startsWith('/de/web/') || options.url.startsWith('/de/file/'))) {
          const originalUrl = options.url;

          // Extract path after /de/web/ or /de/file/
          const pathMatch = options.url.match(/^\/de\/(web|file)\/(.+)$/);
          if (pathMatch) {
            const restPath = pathMatch[2];

            // Rewrite to /api/xsite/... (without link_type parameter)
            options.url = `/api/xsite/${restPath}`;

            eclLog(`[DataTables] 🔄 URL Rewrite: ${originalUrl} → ${options.url}`);
          }
        }
      });
      window.__xsitePrefilterInstalled = true;
      eclLog('[ExternalContentLoader] 🔄 jQuery AJAX URL PreFilter installed');
    }

    // Setup object with CSRF token
    // CRITICAL: Do NOT force contentType - let DataTables set its own (application/x-www-form-urlencoded)
    const ajaxConfig = {
      headers: {
        'X-CSRF-TOKEN': csrfToken
      },
      // Do NOT add data._token here - DataTables includes it automatically in payload
      // Adding it here causes conflicts with DataTables' own _token
    };

    // Add Bearer token to ALL jQuery Ajax requests
    if (authToken) {
      ajaxConfig.beforeSend = function(xhr) {
        xhr.setRequestHeader('Authorization', `Bearer ${authToken}`);
        // Also ensure CSRF token is set (for compatibility)
        if (csrfToken) {
          xhr.setRequestHeader('X-CSRF-TOKEN', csrfToken);
        }

        // CRITICAL: Tell Laravel this is an AJAX request (required for JSON response)
        xhr.setRequestHeader('X-Requested-With', 'XMLHttpRequest');
        xhr.setRequestHeader('Accept', 'application/json, text/javascript, */*; q=0.01');

        // CRITICAL FIX: Add custom Host headers for Laravel domain validation
        // Note: Browser automatically sends 'Host' header - we cannot set it from JavaScript (forbidden)
        // Laravel validates the domain via X-Forwarded-Host/X-Original-Host headers
        const currentHost = window.location.hostname;
        const targetHost = (currentHost === 'localhost' || currentHost === '127.0.0.1' || currentHost === '0.0.0.0')
          ? 'react.truu.com'  // Map localhost → production domain
          : currentHost;

        // ✅ Browser sends 'Host' header automatically - we add custom headers for Laravel
        xhr.setRequestHeader('X-Forwarded-Host', targetHost);
        xhr.setRequestHeader('X-Original-Host', targetHost);

        eclLog(`[DataTables] Setting Host headers: ${targetHost} with X-Requested-With: XMLHttpRequest`);
      };
      eclLog('[ExternalContentLoader] ✅ jQuery AJAX Bearer token configured');
    }

    window.$.ajaxSetup(ajaxConfig);

    if (csrfToken) {
      // eclLog('[ExternalContentLoader] ✅ jQuery AJAX CSRF token updated');
    }
  }
};